"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { cn } from "@/utils/cn";
import { createClient } from "@/utils/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, isValid, parseISO } from "date-fns";
import { X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import slugify from "slugify";
import * as z from "zod";

import { Database } from "@/types/supabase";
// Import UI components
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import ArtistSearch from "@/components/events/ArtistSearch";
import { EventImageUpload } from "@/components/events/EventImageUpload";
import VenueSearch from "@/components/events/VenueSearch";

// Types
type Event = Database["public"]["Tables"]["events"]["Row"] & {
  event_artists?: Array<{
    is_main_artist: boolean;
    artists?: {
      artist: string;
    };
  }>;
};
type EventType = "festival" | "show";

// Form schema
const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  venue: z.string().min(1, "Venue is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  date: z.object({
    from: z.date().nullable(),
    to: z.date().nullable(),
  }),
  img_url: z.string(),
  description: z.string(),
  longitude: z.string(),
  latitude: z.string(),
  published: z.boolean(),
  event_type: z.enum(["festival", "show"]),
  main_artist: z.string(),
  supporting_artists: z.string(),
  address: z.string(),
  ticket: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

interface EventSheetProps {
  children: React.ReactNode;
  event?: Event;
}

// Helper functions
const generateSlug = (
  mainArtist: string | null,
  title: string,
  date: Date,
): string => {
  const formattedDate = date
    .toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
    .toLowerCase();

  const slugParts = mainArtist
    ? [mainArtist, formattedDate]
    : [title, formattedDate];
  return slugify(slugParts.join("-"), { lower: true, strict: true });
};

const formatSupportingArtists = (artists: string): string[] =>
  artists ? artists.split(",").map((s) => s.trim()) : [];

// Main EventSheet component
export function EventSheet({ children, event }: EventSheetProps) {
  // State
  const [open, setOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(
    event?.img_url || null,
  );
  const [eventType, setEventType] = useState<EventType>(
    event?.event_type === "festival" ? "festival" : "show",
  );
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const supabase = createClient();
  const { toast } = useToast();
  const isEditMode = !!event;

  // Memoized main artist extraction
  const mainArtist = useMemo(
    () =>
      event?.event_artists?.find((ea) => ea.is_main_artist)?.artists?.artist,
    [event],
  );

  // Memoized can delete permission check
  const canDelete = useMemo(() => userRole === 3, [userRole]);

  // Memoized canPublishImmediately check
  const canPublishImmediately = useMemo(
    () => userRole === 2 || userRole === 3,
    [userRole],
  );

  // Memoized initial form values
  const defaultValues = useMemo(
    () => ({
      title: event?.title || "",
      venue: event?.venue || "",
      city: event?.city || "",
      state: event?.state || "",
      date: {
        from: event?.date ? parseISO(event.date) : null,
        to: event?.end_date ? parseISO(event.end_date) : null,
      },
      img_url: event?.img_url || "",
      description: event?.description || "",
      longitude: event?.longitude || "",
      latitude: event?.latitude || "",
      published: event?.published || false,
      event_type: (event?.event_type as EventType) || "show",
      main_artist: mainArtist || event?.main_artist || "",
      supporting_artists: Array.isArray(event?.supporting_artists)
        ? event.supporting_artists.join(", ")
        : typeof event?.supporting_artists === "string"
          ? event.supporting_artists.replace(/[\[\]"]/g, "").trim()
          : "",
      address: event?.address || "",
      ticket: event?.ticket || "",
    }),
    [event, mainArtist],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Reset form when event changes
  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
      setCurrentImage(event?.img_url || null);
      setEventType(event?.event_type === "festival" ? "festival" : "show");
    }
  }, [open, event, form, defaultValues]);

  // Fetch user data
  useEffect(() => {
    const fetchUserAndRole = async () => {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (user && !error) {
        setUser(user);
        const { data, error: roleError } = await supabase
          .from("profiles")
          .select("role_id")
          .eq("id", user.id)
          .single();

        if (data && !roleError) {
          setUserRole(data.role_id as number);
        }
      }
    };

    if (open) {
      fetchUserAndRole();
    }
  }, [supabase, open]);

  // Image handling
  const handleImageUpload = useCallback(
    (url: string) => {
      setCurrentImage(url);
      form.setValue("img_url", url, { shouldValidate: true });
    },
    [form],
  );

  const handleDeleteImage = useCallback(() => {
    setCurrentImage(null);
    form.setValue("img_url", "", { shouldValidate: true });
  }, [form]);

  // Event management
  const handleDeleteEvent = useCallback(async () => {
    if (!event) return;

    try {
      const response = await fetch(`/api/events/${event.id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete event");

      setOpen(false);
      toast({
        title: "Success",
        description: "Event deleted successfully!",
      });
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: "Error",
        description: "Failed to delete event. Please try again.",
        variant: "destructive",
      });
    }
  }, [event, toast]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to manage events.",
          variant: "destructive",
        });
        return;
      }

      setIsSubmitting(true);

      const { from: eventDate, to: eventEndDate } = values.date;

      if (!eventDate || !isValid(eventDate)) {
        toast({
          title: "Error",
          description: "Please select a valid start date",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const submissionData = {
        ...values,
        user_id: user.id,
        published: isEditMode ? values.published : canPublishImmediately,
        date: {
          from: eventDate ? format(eventDate, "yyyy-MM-dd") : null,
          to: eventEndDate ? format(eventEndDate, "yyyy-MM-dd") : null,
        },
        slug: generateSlug(
          values.main_artist || null,
          values.title,
          eventDate as Date,
        ),
        supporting_artists: formatSupportingArtists(values.supporting_artists),
      };

      try {
        const response = await fetch(
          isEditMode ? `/api/events/${event!.id}` : "/api/events",
          {
            method: isEditMode ? "PATCH" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(submissionData),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Response error:", {
            status: response.status,
            statusText: response.statusText,
            data: errorData,
          });
          throw new Error(errorData.message || "Failed to process event");
        }

        setOpen(false);

        // Clear form and reset state if it's a new event
        if (!isEditMode) {
          form.reset();
          setCurrentImage(null);
          setEventType("show");
        }

        toast({
          title: "Success",
          description: isEditMode
            ? "Event updated successfully!"
            : userRole === 1
              ? "Thanks for the submission, we're reviewing the event."
              : canPublishImmediately
                ? "Event created and published successfully!"
                : "Event submitted for review successfully!",
        });
      } catch (error) {
        console.error("Error managing event:", error);
        toast({
          title: "Error",
          description: `Failed to ${isEditMode ? "update" : "create"} event. ${error instanceof Error ? error.message : "Please try again."}`,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, userRole, isEditMode, event, form, toast, canPublishImmediately],
  );

  // Render form fields component
  const renderFormFields = () => (
    <Form {...form}>
      <form className="mt-4 space-y-2">
        <FormField
          control={form.control}
          name="event_type"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  onValueChange={(value) => {
                    field.onChange(value);
                    setEventType(value as EventType);
                  }}
                  value={field.value}
                  className="flex gap-4"
                >
                  <FormItem className="flex items-center space-y-0 space-x-1">
                    <FormControl>
                      <RadioGroupItem value="show" />
                    </FormControl>
                    <FormLabel className="font-bold">Show</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-y-0 space-x-1">
                    <FormControl>
                      <RadioGroupItem value="festival" />
                    </FormControl>
                    <FormLabel className="font-bold">Festival</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div
          className={cn(
            "grid gap-2",
            eventType === "show" ? "grid-cols-2" : "grid-cols-1",
          )}
        >
          {eventType === "show" && (
            <FormField
              control={form.control}
              name="main_artist"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main Artist</FormLabel>
                  <FormControl>
                    <ArtistSearch
                      onArtistSelect={({ artist }) => {
                        field.onChange(artist);
                      }}
                      initialValue={field.value || mainArtist || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <FormField
            control={form.control}
            name="venue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Venue</FormLabel>
                <FormControl>
                  <VenueSearch
                    initialValue={event?.venue || ""}
                    onVenueSelect={(venueData) => {
                      form.setValue("venue", venueData.venue, {
                        shouldValidate: true,
                      });
                      form.setValue("address", venueData.address, {
                        shouldValidate: true,
                      });
                      form.setValue("city", venueData.city, {
                        shouldValidate: true,
                      });
                      form.setValue("state", venueData.state, {
                        shouldValidate: true,
                      });
                      form.setValue("latitude", venueData.latitude, {
                        shouldValidate: true,
                      });
                      form.setValue("longitude", venueData.longitude, {
                        shouldValidate: true,
                      });
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>State</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="supporting_artists"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Supporting Artists</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter supporting artists (comma-separated)"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Date Range</FormLabel>
              <DateRangePicker
                value={{
                  from: field.value?.from || undefined,
                  to: field.value?.to || undefined,
                }}
                onChange={(range) => {
                  field.onChange({
                    from: range?.from || null,
                    to: range?.to || null,
                  });
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="img_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Event Image</FormLabel>
              <FormControl>
                <EventImageUpload
                  currentImage={currentImage}
                  onImageUpload={handleImageUpload}
                  onImageDelete={handleDeleteImage}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter event description"
                  {...field}
                  className="h-16"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ticket"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ticket Link</FormLabel>
              <FormControl>
                <Input placeholder="Enter ticket URL" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isEditMode && (userRole === 2 || userRole === 3) && (
          <FormField
            control={form.control}
            name="published"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Published</FormLabel>
                  <FormDescription>
                    This event will be visible to the public
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto p-5 sm:max-w-[600px]"
      >
        <SheetHeader>
          <SheetTitle className="text-2xl font-bold">
            {isEditMode ? "Edit Event" : "Create Event"}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {isEditMode
              ? "Update the event details below."
              : "We rely on the community to add new events, please be as accurate as possible. All submissions are manually reviewed before they're published."}
          </SheetDescription>
        </SheetHeader>

        {renderFormFields()}

        <div className="mt-4 flex items-center justify-between">
          {isEditMode && canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="link" size="sm" className="text-destructive">
                  Delete Event
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the event.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteEvent}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <Button
            type="submit"
            size="sm"
            variant="accent"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="mr-2">
                  {isEditMode ? "Updating..." : "Creating..."}
                </span>
                {/* You can add a spinner component here if you have one */}
              </>
            ) : isEditMode ? (
              "Update Event"
            ) : (
              "Create Event"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
